import { ColumnSizing } from '../tables-context';
import { MRT_ColumnDef } from 'material-react-table';
import { IFormattedSettlementsData } from '../../../interfaces/funds';
import { TableCell } from '../../../components/table/TableCell';
import { Link } from 'react-router-dom';
import React, { FC } from 'react';
import { getFormattedDate } from '../../../utils/utilities';
import { RelatedEntityNameLink } from '../../../components/merchants/RelatedEntityNameLink';
import { CellWithCurrency } from '../../../components/table/CellWithCurrency';
import { getCapturePeriod } from './unposted-batches';
import { useLocation } from 'react-router';
import clsx from 'clsx';

export enum SettlementsTableAccessorKeys {
	payoutDate = 'payoutDate',
	capturePeriod = 'capturePeriodFrom',
	mid = 'mid',
	totalValue = 'totalValue',
	flexChargeFees = 'flexChargeFees',
	chargebacks = 'chargebacks',
	flexChargeChargebackFeesAmount = 'flexChargeChargebackFeesAmount',
	returns = 'returns',
	adjustment = 'adjustment',
	payoutAmount = 'payoutAmount',
	reserveHold = 'reserveHold',
	reserveRelease = 'reserveRelease',
	reserveUtilization = 'reserveUtilization',
	sender = 'senderRelatedEntityId',
	receiver = 'receiverRelatedEntityId',
	batchType = 'batchType',
}

export const defaultSettlementsColumnSizing: ColumnSizing = {
	[SettlementsTableAccessorKeys.payoutDate]: 100,
	[SettlementsTableAccessorKeys.capturePeriod]: 100,
	[SettlementsTableAccessorKeys.batchType]: 50,
	[SettlementsTableAccessorKeys.sender]: 100,
	[SettlementsTableAccessorKeys.receiver]: 100,
	[SettlementsTableAccessorKeys.totalValue]: 100,
	[SettlementsTableAccessorKeys.flexChargeFees]: 100,
	[SettlementsTableAccessorKeys.chargebacks]: 100,
	[SettlementsTableAccessorKeys.flexChargeChargebackFeesAmount]: 100,
	[SettlementsTableAccessorKeys.returns]: 100,
	[SettlementsTableAccessorKeys.adjustment]: 100,
	[SettlementsTableAccessorKeys.reserveHold]: 100,
	[SettlementsTableAccessorKeys.reserveRelease]: 100,
	[SettlementsTableAccessorKeys.reserveUtilization]: 100,
	[SettlementsTableAccessorKeys.payoutAmount]: 100,
};


interface IProps {
	children: any;
	settlementId: string;
	additionalClass?: string;
}

export const SettlementsLink: FC<IProps> = (props) => {
	const { children, settlementId, additionalClass } = props;
	const { pathname, search } = useLocation();
	const baseUrl = pathname.includes('funds') ? '/funds/settlements' : '/settlements';
	return (
		<Link
			to={`${baseUrl}/${settlementId}`}
			state={{ returnUrl: `${pathname}${search}` }}
			className={clsx('text-black fs-8 fc-text-overflow', additionalClass)}
		>
			{children}
		</Link>
	);
};

export const defaultSettlementsColumns: MRT_ColumnDef<IFormattedSettlementsData>[] = [
	{
		accessorKey: SettlementsTableAccessorKeys.payoutDate,
		header: 'Payout date',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Payout date</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<SettlementsLink settlementId={row?.original?.id}>
					{getFormattedDate(renderedCellValue, 'MMM dd, yyyy')}
				</SettlementsLink>
			</TableCell>
		)
	},
	{
		accessorKey: SettlementsTableAccessorKeys.capturePeriod,
		header: 'Capture period',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Capture period</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<SettlementsLink settlementId={row?.original?.id}>
					{getCapturePeriod(renderedCellValue, row?.original?.capturePeriodTo)}
				</SettlementsLink>
			</TableCell>
		)
	},
	{
		accessorKey: SettlementsTableAccessorKeys.batchType,
		header: 'Type',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Type</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<SettlementsLink settlementId={row?.original?.id}>
					{renderedCellValue ? renderedCellValue : '-'}
				</SettlementsLink>
			</TableCell>
		)
	},
	{
		accessorKey: SettlementsTableAccessorKeys.sender,
		header: 'Sender',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Sender</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<RelatedEntityNameLink
					name={row?.original?.senderRelatedEntityDba}
					id={renderedCellValue}
					type={row?.original?.senderRelatedEntityType}
				/>
			</ TableCell>
		)
	},
	{
		accessorKey: SettlementsTableAccessorKeys.receiver,
		header: 'Receiver',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Receiver</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<RelatedEntityNameLink
					name={row?.original?.receiverRelatedEntityDba}
					id={renderedCellValue}
					type={row?.original?.receiverRelatedEntityType}
				/>
			</ TableCell>
		)
	},
	{
		accessorKey: SettlementsTableAccessorKeys.totalValue,
		header: 'Total value',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Total value</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<SettlementsLink settlementId={row?.original?.id}>
					<CellWithCurrency
						value={renderedCellValue}
						currencySymbol={row?.original?.currencySymbol}
						currencyCode={row?.original?.currency}
						isPositive={renderedCellValue >= 0}
					/>
				</SettlementsLink>
			</TableCell>
		)
	},
	{
		accessorKey: SettlementsTableAccessorKeys.flexChargeFees,
		header: 'Processing fees',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Processing fees</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<SettlementsLink settlementId={row?.original?.id}>
					<CellWithCurrency
						value={renderedCellValue}
						currencySymbol={row?.original?.currencySymbol}
						currencyCode={row?.original?.currency}
						isPositive={renderedCellValue >= 0}
					/>
				</SettlementsLink>
			</TableCell>
		)
	},
	{
		accessorKey: SettlementsTableAccessorKeys.flexChargeChargebackFeesAmount,
		header: 'Chargebacks fees',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Chargebacks fees</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<SettlementsLink settlementId={row?.original?.id}>
					<CellWithCurrency
						value={renderedCellValue}
						currencySymbol={row?.original?.currencySymbol}
						currencyCode={row?.original?.currency}
						isPositive={renderedCellValue >= 0}
					/>
				</SettlementsLink>
			</TableCell>
		)
	},
	{
		accessorKey: SettlementsTableAccessorKeys.chargebacks,
		header: 'Chargebacks value',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Chargebacks value</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<SettlementsLink settlementId={row?.original?.id}>
					<CellWithCurrency
						value={renderedCellValue}
						currencySymbol={row?.original?.currencySymbol}
						currencyCode={row?.original?.currency}
						isPositive={renderedCellValue >= 0}
					/>
				</SettlementsLink>
			</TableCell>
		)
	},
	{
		accessorKey: SettlementsTableAccessorKeys.returns,
		header: 'Returns',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Returns</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<SettlementsLink settlementId={row?.original?.id}>
					<CellWithCurrency
						value={renderedCellValue}
						currencySymbol={row?.original?.currencySymbol}
						currencyCode={row?.original?.currency}
						isPositive={renderedCellValue >= 0}
					/>
				</SettlementsLink>
			</TableCell>
		)
	},
	{
		accessorKey: SettlementsTableAccessorKeys.adjustment,
		header: 'Adjustment',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Adjustment</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<SettlementsLink settlementId={row?.original?.id}>
					<CellWithCurrency
						value={renderedCellValue}
						currencySymbol={row?.original?.currencySymbol}
						currencyCode={row?.original?.currency}
						isPositive={renderedCellValue >= 0}
					/>
				</SettlementsLink>
			</TableCell>
		)
	},
	{
		accessorKey: SettlementsTableAccessorKeys.reserveHold,
		header: 'Reserve Hold',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Reserve Hold</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => {
			return (
				<TableCell
					additionalClass="d-flex align-items-center main-font-family text-black"
					density={table?.options?.state?.density}
					isTextOverflow={true}
				>
					<SettlementsLink settlementId={row?.original?.id}>
						<CellWithCurrency
							value={renderedCellValue}
							currencySymbol={row?.original?.currencySymbol}
							currencyCode={row?.original?.currency}
							isPositive={renderedCellValue >= 0}
						/>
					</SettlementsLink>
				</TableCell>
			);
		}
	},
	{
		accessorKey: SettlementsTableAccessorKeys.reserveRelease,
		header: 'Reserve Release',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Reserve Release</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => {
			return (
				<TableCell
					additionalClass="d-flex align-items-center main-font-family text-black"
					density={table?.options?.state?.density}
					isTextOverflow={true}
				>
					<SettlementsLink settlementId={row?.original?.id}>
						<CellWithCurrency
							value={renderedCellValue}
							currencySymbol={row?.original?.currencySymbol}
							currencyCode={row?.original?.currency}
							isPositive={renderedCellValue >= 0}
						/>
					</SettlementsLink>
				</TableCell>
			);
		}
	},
	{
		accessorKey: SettlementsTableAccessorKeys.reserveUtilization,
		header: 'Reserve Utilization',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Reserve Utilization</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => {
			return (
				<TableCell
					additionalClass="d-flex align-items-center main-font-family text-black"
					density={table?.options?.state?.density}
					isTextOverflow={true}
				>
					<SettlementsLink settlementId={row?.original?.id}>
						<CellWithCurrency
							value={renderedCellValue}
							currencySymbol={row?.original?.currencySymbol}
							currencyCode={row?.original?.currency}
							isPositive={renderedCellValue >= 0}
						/>
					</SettlementsLink>
				</TableCell>
			);
		}
	},
	{
		accessorKey: SettlementsTableAccessorKeys.payoutAmount,
		header: 'Payout',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Payout</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<SettlementsLink settlementId={row?.original?.id}>
					<CellWithCurrency
						value={renderedCellValue}
						currencySymbol={row?.original?.currencySymbol}
						currencyCode={row?.original?.currency}
						isPositive={renderedCellValue >= 0}
					/>
				</SettlementsLink>
			</TableCell>
		)
	},
];
